// Pure helpers for AppHost.vue's deep-link detection. Framework-free (no `this`,
// no Vue) so they can be unit-tested directly -- see deepLinkPath.spec.js.
//
// Background (MYS-7185): a route's matched wildcard sub-path (/app/:appName/:path(.*))
// is only a genuine deep link if it isn't simply the tail of the resolved app's OWN
// Contentful `link` field. Some apps' `link` already includes trailing segments beyond
// their `/app/:appName` root (e.g. billing-manager's was `/app/billingManager/billing`,
// a stale leftover that was harmless before MYS-6945 started treating wildcard sub-paths
// as deep links). Diffing the matched path against the app's own link avoids misreading
// that stale tail as a deep link into the guest app.

function normalizePath(path) {
  return (path ?? '').replace(/\/+$/, '')
}

function toSegments(path) {
  return normalizePath(path).split('/').filter(Boolean)
}

// Segments of `app`'s own canonical link beyond the `/app/:appPath` root, e.g.
// billing-manager's stale link '/app/billingManager/billing' -> ['billing'];
// trade-hub-app's plain link '/app/trade-hub' -> [].
// Returns [] if `app` is unresolved, or its link doesn't even share the
// `/app/:appPath` root (defensive against inconsistent Contentful data) -- callers
// then fall back to treating the full matched wildcard path as a genuine deep link,
// matching pre-existing behavior.
export function getAppLinkExtraSegments(app, appPath) {
  const linkSegments = toSegments(app?.fields?.link?.de)
  const rootSegments = toSegments(`/app/${appPath}`)

  const isPrefixed = rootSegments.length <= linkSegments.length &&
    rootSegments.every((segment, i) => linkSegments[i] === segment)

  return isPrefixed ? linkSegments.slice(rootSegments.length) : []
}

// Removes `extraSegments` (the app's own trailing link segments) from the front of
// the matched wildcard sub-path `matchedPath`, segment-by-segment -- not naive string
// slicing, so a segment like 'billing2' never falsely "contains" 'billing'. Returns ''
// on a full overlap (nothing genuine left), the remainder on partial overlap, or
// `matchedPath` unchanged if there's no full segment-wise overlap at all.
export function stripAppLinkPrefix(matchedPath, extraSegments) {
  if (!extraSegments?.length) return matchedPath ?? ''

  const matchedSegments = toSegments(matchedPath)
  const overlapsFully = extraSegments.length <= matchedSegments.length &&
    extraSegments.every((segment, i) => matchedSegments[i] === segment)

  return overlapsFully ? matchedSegments.slice(extraSegments.length).join('/') : (matchedPath ?? '')
}

// Resolves an app from `allApplications` by its `link` field, matching `pathname`
// (e.g. '/app/billingManager'). Tries an exact match first (unambiguous, the common
// case), then falls back to a segment-array prefix match -- not string `startsWith`,
// to avoid '/app/billingManager2' falsely matching '/app/billingManager' -- so apps
// whose `link` has extra trailing segments can still be resolved on a direct/shared
// link load (previously this fallback was exact-match-only and would leave `app`
// unresolved entirely for such apps unless `selectedComponent`/store was already
// populated some other way).
export function resolveAppByLink(allApplications, pathname) {
  const targetSegments = toSegments(pathname)

  const exact = allApplications.find(app => normalizePath(app.fields.link?.de) === normalizePath(pathname))
  if (exact) return exact

  return allApplications.find(app => {
    const linkSegments = toSegments(app.fields.link?.de)
    return linkSegments.length > targetSegments.length &&
      targetSegments.every((segment, i) => linkSegments[i] === segment)
  })
}
